// Дни и часове на резервациите — чисти функции без React. Всичко е в
// локалното време на терминала (заведението е в Europe/Sofia, терминалите —
// също); `Date` сам се грижи за смяната на лятно/зимно време.

export const MINUTE_MS = 60_000;

// Времевата линия: от 11:00 (или по-рано — виж `timelineRange`) до 24:00
// през 5 минути.
export const TIMELINE_DEFAULT_START_MINUTE = 11 * 60;
export const TIMELINE_END_MINUTE = 24 * 60;
export const TIMELINE_STEP_MINUTES = 5;

/** Обхватът на плъзгача за деня: [startMinute, endMinute] + надписите (часове). */
export interface TimelineRange {
  startMinute: number;
  endMinute: number;
  tickHours: number[];
}

/**
 * Надписите: на всеки 2 часа, докато линията е до 16 часа (11, 13 … 23;
 * 9, 11 … 23), на всеки 3 — при по-дълга (0, 3 … 21), за да не се сбиват.
 * 24:00 (краят) не се надписва.
 */
export const timelineTickHours = (startMinute: number): number[] => {
  const startHour = Math.floor(startMinute / 60);
  const step = 24 - startHour <= 16 ? 2 : 3;
  const hours: number[] = [];
  for (let hour = startHour; hour < 24; hour += step) hours.push(hour);
  return hours;
};

/**
 * Обхватът на линията за деня: от по-ранното от 11:00 и най-ранния момент на
 * деня (резервация, настаняване, отворена сметка, „сега"), закръглено надолу
 * до час, до 24:00. Часът на заведението не е ограничен (дискотека с
 * резервации след полунощ — те са в деня на датата си, от 00:00).
 */
export const timelineRange = (dayMinutes: number[]): TimelineRange => {
  const inDay = dayMinutes.filter((minute) => minute >= 0 && minute < TIMELINE_END_MINUTE);
  const earliest = Math.min(TIMELINE_DEFAULT_START_MINUTE, ...inDay);
  const startMinute = Math.floor(earliest / 60) * 60;
  return { startMinute, endMinute: TIMELINE_END_MINUTE, tickHours: timelineTickHours(startMinute) };
};

export const DEFAULT_TIMELINE_RANGE = timelineRange([]);

const pad = (value: number) => String(value).padStart(2, "0");

/** Локалният ден на момента като „YYYY-MM-DD" (форматът на `?date=`). */
export const toDateKey = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** „YYYY-MM-DD" → локална полунощ на деня. */
export const parseDateKey = (dateKey: string): Date => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (dateKey: string, days: number): string => {
  const date = parseDateKey(dateKey);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

/** Моментът „минута N от деня" (N може да е 24*60 = полунощ на следващия). */
export const momentAtMinute = (dateKey: string, minuteOfDay: number): number => {
  const date = parseDateKey(dateKey);
  date.setMinutes(minuteOfDay);
  return date.getTime();
};

/** Минутата от деня на момента (часовникът на стената, без секундите). */
export const minuteOfDay = (moment: number): number => {
  const date = new Date(moment);
  return date.getHours() * 60 + date.getMinutes();
};

/** Краят на локалния ден на момента (полунощ след него). */
export const endOfLocalDay = (moment: number): number => {
  const date = new Date(moment);
  date.setHours(24, 0, 0, 0);
  return date.getTime();
};

/** Плъзгачът стои в обхвата на линията, дори когато моментът е извън него. */
export const clampToTimeline = (minute: number, range: TimelineRange = DEFAULT_TIMELINE_RANGE): number =>
  Math.min(range.endMinute, Math.max(range.startMinute, minute));

/** Надолу до стъпката на плъзгача (19:13 → 19:10). */
export const snapToStep = (minute: number): number =>
  Math.floor(minute / TIMELINE_STEP_MINUTES) * TIMELINE_STEP_MINUTES;

/** „19:05" за момент. */
export const formatClock = (moment: number): string => {
  const date = new Date(moment);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Часът на момента като част от деня `dateKey`: полунощта в края му (минута
 * 1440 = 00:00 на следващия) е „24:00", иначе — като `formatClock`.
 */
export const formatClockOfDay = (moment: number, dateKey: string): string =>
  moment === momentAtMinute(dateKey, 24 * 60) ? formatMinuteOfDay(24 * 60) : formatClock(moment);

/** „19:05" за минута от деня (1440 → „24:00"). */
export const formatMinuteOfDay = (minute: number): string =>
  `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;

/** Цели минути от `from` до `to` (надолу; отрицателното става 0). */
export const minutesBetween = (from: number, to: number): number =>
  Math.max(0, Math.floor((to - from) / MINUTE_MS));

/** 75 → { hours: 1, minutes: 15 } — за „1 ч 15 мин". */
export const splitDuration = (totalMinutes: number) => ({
  hours: Math.floor(totalMinutes / 60),
  minutes: totalMinutes % 60,
});
